/**
 * ============================================================================
 * DEVICE CAPABILITY DETECTOR - Universal Device Support System
 * Ensures sticky scroll works flawlessly on ALL devices
 * ============================================================================
 */

class DeviceCapabilityDetector {
  constructor() {
    this.capabilities = {
      // Performance tier: 'high' | 'medium' | 'low'
      tier: null,
      // Individual capabilities
      webGL: false,
      gpu: false,
      touch: false,
      mobile: false,
      tablet: false,
      connection: 'unknown',
      battery: {
        level: 100,
        charging: true,
        supported: false
      },
      // Device specs
      devicePixelRatio: window.devicePixelRatio || 1,
      screenSize: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      // Browser features
      intersectionObserver: 'IntersectionObserver' in window,
      requestIdleCallback: 'requestIdleCallback' in window,
      // Reduced motion preference
      prefersReducedMotion: false,
      // Orientation
      orientation: this.getOrientation(),
      // CPU cores (if available)
      cpuCores: navigator.hardwareConcurrency || 2
    };

    this.init();
  }

  init() {
    this.detectWebGL();
    this.detectGPU();
    this.detectTouch();
    this.detectDevice();
    this.detectConnection();
    this.detectBattery();
    this.detectReducedMotion();
    this.calculateTier();
    this.setupListeners();
  }

  // ===== DETECTION METHODS =====

  detectWebGL() {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      this.capabilities.webGL = !!gl;
      
      if (gl) {
        // Get GPU renderer info for additional insights
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (debugInfo) {
          const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
          this.capabilities.gpuRenderer = renderer;
          
          // Detect low-end GPUs
          const lowEndGPUs = ['mali-400', 'mali-450', 'adreno 3', 'adreno 4', 'powervr sgx'];
          this.capabilities.isLowEndGPU = lowEndGPUs.some(gpu => 
            renderer.toLowerCase().includes(gpu)
          );
        }
      }
    } catch (e) {
      this.capabilities.webGL = false;
    }
  }

  detectGPU() {
    // Check for GPU acceleration support
    this.capabilities.gpu = 
      'hardwareAcceleration' in navigator || 
      this.capabilities.webGL;
  }

  detectTouch() {
    this.capabilities.touch = 
      'ontouchstart' in window || 
      navigator.maxTouchPoints > 0 || 
      navigator.msMaxTouchPoints > 0;
  }

  detectDevice() {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    
    // Mobile detection
    this.capabilities.mobile = /iPhone|iPod|Android|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    
    // Tablet detection
    this.capabilities.tablet = /iPad|Android(?!.*Mobile)|Tablet/i.test(ua);
    
    // iOS detection
    this.capabilities.iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    
    // Android detection
    this.capabilities.android = /Android/i.test(ua);
    
    // Specific device detection for known problematic devices
    this.capabilities.oldAndroid = /Android [2-4]/.test(ua);
    this.capabilities.oldIOS = /OS [6-9]_/.test(ua);
  }

  detectConnection() {
    if ('connection' in navigator || 'mozConnection' in navigator || 'webkitConnection' in navigator) {
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      
      if (conn) {
        this.capabilities.connection = conn.effectiveType || 'unknown';
        this.capabilities.saveData = conn.saveData || false;
        this.capabilities.downlink = conn.downlink || null;
        
        // Listen for connection changes
        conn.addEventListener('change', () => {
          this.capabilities.connection = conn.effectiveType;
          this.calculateTier();
          this.emitCapabilityChange();
        });
      }
    }
  }

  async detectBattery() {
    if ('getBattery' in navigator) {
      try {
        const battery = await navigator.getBattery();
        this.capabilities.battery = {
          level: battery.level * 100,
          charging: battery.charging,
          supported: true
        };

        // Listen for battery changes
        battery.addEventListener('levelchange', () => {
          this.capabilities.battery.level = battery.level * 100;
          this.calculateTier();
          this.emitCapabilityChange();
        });

        battery.addEventListener('chargingchange', () => {
          this.capabilities.battery.charging = battery.charging;
          this.calculateTier();
          this.emitCapabilityChange();
        });
      } catch (e) {
        console.warn('Battery API not available:', e);
      }
    }
  }

  detectReducedMotion() {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.capabilities.prefersReducedMotion = mediaQuery.matches;
    
    // Listen for changes
    mediaQuery.addEventListener('change', (e) => {
      this.capabilities.prefersReducedMotion = e.matches;
      this.calculateTier();
      this.emitCapabilityChange();
    });
  }

  getOrientation() {
    if (window.innerWidth > window.innerHeight) {
      return 'landscape';
    } else {
      return 'portrait';
    }
  }

  // ===== TIER CALCULATION =====

  calculateTier() {
    let score = 0;
    const caps = this.capabilities;

    // Base scores
    if (caps.webGL) score += 20;
    if (caps.gpu) score += 15;
    if (caps.cpuCores >= 4) score += 15;
    if (caps.cpuCores >= 8) score += 10;

    // Device type penalties
    if (caps.mobile) score -= 10;
    if (caps.oldAndroid || caps.oldIOS) score -= 30;
    if (caps.isLowEndGPU) score -= 20;

    // Connection penalties
    if (caps.connection === 'slow-2g' || caps.connection === '2g') score -= 20;
    if (caps.connection === '3g') score -= 10;
    if (caps.saveData) score -= 15;

    // Battery penalties (only if not charging)
    if (!caps.battery.charging && caps.battery.level < 20) score -= 25;
    if (!caps.battery.charging && caps.battery.level < 50) score -= 10;

    // Screen size considerations
    if (caps.screenSize.width < 768) score -= 5;
    if (caps.screenSize.width < 480) score -= 10;

    // Reduced motion override
    if (caps.prefersReducedMotion) {
      caps.tier = 'minimal';
      return;
    }

    // Calculate final tier
    if (score >= 40) {
      caps.tier = 'high';
    } else if (score >= 15) {
      caps.tier = 'medium';
    } else if (score >= -10) {
      caps.tier = 'low';
    } else {
      caps.tier = 'minimal';
    }

    console.log(`[DeviceCapabilityDetector] Tier: ${caps.tier} (Score: ${score})`);
  }

  // ===== EVENT LISTENERS =====

  setupListeners() {
    // Orientation change
    window.addEventListener('orientationchange', () => {
      this.handleOrientationChange();
    });

    // Resize (also catches orientation changes)
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.handleResize();
      }, 150);
    });

    // Visibility change (tab switching)
    document.addEventListener('visibilitychange', () => {
      this.handleVisibilityChange();
    });

    // Page visibility for battery saving
    if ('hidden' in document) {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.emitEvent('page-hidden');
        } else {
          this.emitEvent('page-visible');
        }
      });
    }
  }

  handleOrientationChange() {
    const oldOrientation = this.capabilities.orientation;
    const newOrientation = this.getOrientation();
    
    if (oldOrientation !== newOrientation) {
      this.capabilities.orientation = newOrientation;
      this.capabilities.screenSize = {
        width: window.innerWidth,
        height: window.innerHeight
      };
      
      console.log(`[DeviceCapabilityDetector] Orientation changed: ${oldOrientation} → ${newOrientation}`);
      this.emitEvent('orientation-change', { 
        oldOrientation, 
        newOrientation 
      });
    }
  }

  handleResize() {
    this.capabilities.screenSize = {
      width: window.innerWidth,
      height: window.innerHeight
    };
    
    this.emitEvent('screen-resize', this.capabilities.screenSize);
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.emitEvent('tab-hidden');
    } else {
      this.emitEvent('tab-visible');
    }
  }

  emitCapabilityChange() {
    this.emitEvent('capability-change', this.capabilities);
  }

  emitEvent(eventName, detail = {}) {
    window.dispatchEvent(new CustomEvent(`device-capability:${eventName}`, { 
      detail: { ...detail, capabilities: this.capabilities }
    }));
  }

  // ===== PUBLIC API =====

  getCapabilities() {
    return this.capabilities;
  }

  getTier() {
    return this.capabilities.tier;
  }

  shouldUse3D() {
    return this.capabilities.tier === 'high' || this.capabilities.tier === 'medium';
  }

  shouldUseCanvas() {
    return this.capabilities.tier !== 'minimal';
  }

  getStarCount() {
    switch (this.capabilities.tier) {
      case 'high':
        return 100;
      case 'medium':
        return 60;
      case 'low':
        return 30;
      case 'minimal':
        return 0;
      default:
        return 50;
    }
  }

  getFrameThrottle() {
    switch (this.capabilities.tier) {
      case 'high':
        return 16; // 60fps
      case 'medium':
        return 33; // 30fps
      case 'low':
        return 50; // 20fps
      case 'minimal':
        return 100; // 10fps
      default:
        return 33;
    }
  }

  shouldUseBlur() {
    // Blur is expensive on mobile
    return this.capabilities.tier === 'high' && !this.capabilities.mobile;
  }

  getBlurAmount() {
    switch (this.capabilities.tier) {
      case 'high':
        return 20;
      case 'medium':
        return 10;
      case 'low':
        return 5;
      default:
        return 0;
    }
  }

  shouldUseParallax() {
    return this.capabilities.tier !== 'minimal';
  }

  getAnimationDuration() {
    switch (this.capabilities.tier) {
      case 'high':
        return 1.0; // Full duration
      case 'medium':
        return 0.7; // 70% of normal
      case 'low':
        return 0.5; // 50% of normal
      case 'minimal':
        return 0.3; // 30% of normal (snap)
      default:
        return 1.0;
    }
  }

  // Quality settings for different tiers
  getQualitySettings() {
    const tier = this.capabilities.tier;
    
    const settings = {
      high: {
        starCount: 100,
        use3D: true,
        useCanvas: true,
        useBlur: true,
        maxBlur: 20,
        useDepthOfField: true,
        useShadows: true,
        useParticles: true,
        frameRate: 60,
        animationDuration: 1.0,
        imageQuality: 'high',
        enableHyperjump: true,
        hyperjumpIntensity: 1.0
      },
      medium: {
        starCount: 60,
        use3D: true,
        useCanvas: true,
        useBlur: true,
        maxBlur: 10,
        useDepthOfField: true,
        useShadows: false,
        useParticles: false,
        frameRate: 30,
        animationDuration: 0.8,
        imageQuality: 'medium',
        enableHyperjump: true,
        hyperjumpIntensity: 0.7
      },
      low: {
        starCount: 30,
        use3D: false,
        useCanvas: true,
        useBlur: false,
        maxBlur: 5,
        useDepthOfField: false,
        useShadows: false,
        useParticles: false,
        frameRate: 20,
        animationDuration: 0.6,
        imageQuality: 'low',
        enableHyperjump: false,
        hyperjumpIntensity: 0.3
      },
      minimal: {
        starCount: 0,
        use3D: false,
        useCanvas: false,
        useBlur: false,
        maxBlur: 0,
        useDepthOfField: false,
        useShadows: false,
        useParticles: false,
        frameRate: 10,
        animationDuration: 0.3,
        imageQuality: 'low',
        enableHyperjump: false,
        hyperjumpIntensity: 0
      }
    };

    return settings[tier] || settings.medium;
  }

  // Debug info
  getDebugInfo() {
    return {
      tier: this.capabilities.tier,
      device: {
        mobile: this.capabilities.mobile,
        tablet: this.capabilities.tablet,
        iOS: this.capabilities.iOS,
        android: this.capabilities.android
      },
      capabilities: {
        webGL: this.capabilities.webGL,
        gpu: this.capabilities.gpu,
        cpuCores: this.capabilities.cpuCores
      },
      connection: this.capabilities.connection,
      battery: this.capabilities.battery,
      screen: this.capabilities.screenSize,
      orientation: this.capabilities.orientation,
      reducedMotion: this.capabilities.prefersReducedMotion
    };
  }

  logDebugInfo() {
    console.group('🔍 Device Capability Report');
    console.log('Performance Tier:', this.capabilities.tier);
    console.log('Quality Settings:', this.getQualitySettings());
    console.log('Full Capabilities:', this.getDebugInfo());
    console.groupEnd();
  }
}

// Create singleton instance
window.DeviceCapabilityDetector = window.DeviceCapabilityDetector || new DeviceCapabilityDetector();

// Log on load (can be disabled in production)
if (window.location.hostname === 'localhost' || window.location.search.includes('debug=true')) {
  window.DeviceCapabilityDetector.logDebugInfo();
}

